import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import tracking from '@g4/g4-analytics';
import moment from '@g4/moment-utc';
import { hook, withValue } from '@g4/automation-hooks';
import get from 'lodash/get';

import GAClickEventTracker from '../../../../../components/GAClickEventTracker';
import { useTranslations } from './../../../../../graphql/components/TranslationsProvider';
import { useBookingPath, useCart, useSSRQuery } from '../../../../../graphql';
import {
  VerticalSpacing,
  Box,
  Text,
  Popover,
  Media,
  VisuallyHidden,
  Spinner,
} from '../../../../../components/Boneyard';
import Price from '@g4/next-furniture/Price/Price';
import Markdown from '../../../../../components/Markdown';
import FlightOperator from '../../../../../components/FlightOperator';
import PopoverContent from './flightItem/PopoverContent';
import FLIGHT_ITEM_QUERY from './flightItem/flightItem.query.graphql.js';

import PLANE_ICON from '../../../../../images/plane-icon.svg';
import OVERNIGHT_ICON from '../../../../../images/overnight-icon.svg';
import {
  DEPARTING,
  RETURNING,
  WARNING_DEPARTING_AFTER_RETURNING,
} from '../../../constants';
import { sortOrder } from '../../../../../utils/constants/trackingConstants';

import {
  FLIGHT_NUMBER_LABEL,
  FLIGHT_NUMBER,
  FLIGHT_DEPARTURE_LABEL,
  FLIGHT_DEPARTURE_TIME,
  FLIGHT_ARRIVAL_LABEL,
  FLIGHT_ARRIVAL_TIME,
  SEATS_AVAILABILITY_TEXT,
  SEATS_AVAILABLE_TEXT,
  STRIKETHROUGH_FLIGHT_PRICE,
  FLIGHT_PRICE,
  LOYALTY_DISCOUNTED_FLIGHT_PRICE,
  SELECTED_FLIGHT,
  UNSELECTED_FLIGHT,
  PRICE_BOX,
  OVERNIGHT_FLIGHT_LABEL,
  OTP_POPOVER,
  DISABLED_FLIGHT,
} from '../../../../../../automationHooks.json';
import getFlightsRawProducts from '../../../getFlightsRawProducts';
import { getAffinityString } from '../../../../app/trackingItems.js';

const propTypes = {
  isFlightsQueryLoading: PropTypes.bool.isRequired,
  theme: PropTypes.object,
  flightOption: PropTypes.object.isRequired,
  setFlightSelected: PropTypes.func,
  selected: PropTypes.bool,
  leg: PropTypes.oneOf(['departing', 'returning']),
  disableFlightIfSameDayBasedOnTime: PropTypes.bool,
  warning: PropTypes.object,
  deactivateWarning: PropTypes.func,
  providerId: PropTypes.string,
  isRedemptionEnabledForNavitaire: PropTypes.bool,
  flightSearchCriteria: PropTypes.object,
  googleAnalyticsData: PropTypes.object,
};

const FlightBox = styled(Box)`
  display: flex;
`;

const FlightWrapper = styled.div`
  cursor: pointer;
  border-top: 2px solid
    ${props => props.theme.colors.flights.flightItem.divider};

  &:last-of-type {
    border-bottom: 2px solid
      ${props => props.theme.colors.flights.flightItem.divider};
  }
`;

const FlightNumberBox = styled(Box)`
  flex-basis: 77px;
  flex-grow: 0;

  ${props => props.theme.query.md} {
    flex-basis: 130px;
  }
`;

const FlightNumberText = styled(Text)`
  text-decoration: underline;
`;

const LabelHeader = styled.p`
  font-weight: 600;
  font-size: ${props => props.theme.toRem(16)};

  ${props => props.theme.query.md} {
    font-size: ${props => props.theme.toRem(16)};
  }
`;
const FlightTime = styled.p`
  color: ${props => props.theme.colors.flights.flightItem.lightBlue};
  font-size: ${props => props.theme.toRem(18)};
  font-weight: 600;

  > span {
    font-size: ${props => props.theme.toRem(12)};
    font-weight: normal;
    padding-left: ${props => props.theme.spacing(1)};
  }

  ${props => props.theme.query.md} {
    font-size: ${props => props.theme.toRem(28)};
    > span {
      font-size: ${props => props.theme.toRem(18)};
    }
  }
`;
const FlightDetailsBox = styled(Box)`
  flex-grow: 1;

  ${/* sc-selector */ Box} {
    flex-grow: 1;
  }
`;

const TimeBox = styled(Box)`
  flex-basis: 100%;
  align-self: center;

  ${props => props.theme.query.md} {
    flex-basis: 60%;
  }
`;

const SeatsTextBox = styled(Box)`
  align-self: center;
  flex-basis: 100%;

  ${/* sc-selector */ Text} {
    max-width: 100%;
    margin: 0 auto;
  }

  ${props => props.theme.query.md} {
    flex-basis: 40%;

    ${/* sc-selector */ Text} {
      max-width: 220px;
    }
  }
`;

const DepartureBox = styled(Box)`
  flex-basis: 30%;
  align-self: flex-start;
`;

const ArrivalBox = styled(Box)`
  flex-basis: 70%;
  padding-left: ${props => props.theme.spacing(6)};
  background: url(${PLANE_ICON}) no-repeat left top;
  background-size: 20px;
  align-self: flex-start;

  ${props => props.theme.query.md} {
    padding-left: ${props => props.theme.spacing(14)};
    margin-left: ${props => props.theme.spacing(4)};
    background-position: left center;
    background-size: 40px;
  }

  ${props => props.theme.query.lg} {
    padding-left: ${props => props.theme.spacing(18)};
  }
`;
const OvernightText = styled(Text)`
  line-height: ${props => props.theme.toRem(10)};
  padding-left: ${props => props.theme.toRem(12)};
  margin-top: ${props => props.theme.toRem(2)};
  background: url(${OVERNIGHT_ICON}) no-repeat left center;
  background-size: 10px;

  ${props => props.theme.query.md} {
    background-size: 15px;
    line-height: ${props => props.theme.toRem(15)};
    padding-left: ${props => props.theme.toRem(18)};
    margin-top: ${props => props.theme.toRem(5)};
  }
`;

/* stylelint-disable no-descending-specificity */
const SelectFlightBox = styled(Box)`
  flex-basis: ${({ theme }) => theme.flights.list.priceWidth.mobile};
  flex-shrink: 0;
  background-color: ${props =>
    props.theme.colors.flights.flightItem.lightBackground};
  ${props =>
    props.selected &&
    `
    background-image: ${props.theme.colors.flights.flightItem.selected};
  `}

  ${props => props.theme.query.md} {
    flex-basis: ${({ theme }) => theme.flights.list.priceWidth.midSized};
  }

  ${props => props.theme.query.lg} {
    flex-basis: ${({ theme }) => theme.flights.list.priceWidth.desktop};
  }
`;

const PriceBox = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  padding: ${({ theme: { spacing } }) => `0 ${spacing(1)} ${spacing(1)} 0`};
  color: ${props => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
  display: flex;
  justify-content: center;
  flex-direction: column;

  > p {
    position: relative;
    font-size: ${props => props.theme.toRem(12)};
    padding-top: ${props => props.theme.spacing(2)};
    padding-bottom: ${props => props.theme.spacing(1)};
  }

  .prices {
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      &:last-of-type {
        margin-left: ${props => props.theme.spacing(1)};
      }

      del {
        /* stylelint-disable-next-line declaration-no-important */
        position: relative;
        display: inline-block;
        text-decoration-line: none; /* we must override the native strikethrough line and add our custom one */

        &:after {
          position: absolute;
          content: '';
          left: 0;
          top: 50%;
          right: 0;
          border-top: 2px solid;
          border-color: inherit;
          transform: rotate(-25deg);
        }
      }
    }
  }

  ${props => props.theme.query.md} {
    > p {
      font-size: ${props => props.theme.toRem(16)};
    }
  }

  ${props => props.theme.query.lg} {
    display: flex;
    padding: ${({ theme: { spacing } }) => `${spacing(6)} 0 ${spacing(1)}`};

    > p {
      position: absolute;
      left: 0;
      top: ${props => props.theme.spacing(3)};
      width: 100%;
      padding: 0 ${props => props.theme.spacing(4)};
      font-weight: 600;
    }
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  top: auto;
  left: -10000px;
  height: 1px;
  width: 1px;
  overflow: hidden;

  &:focus + ${/* sc-selector */ FlightBox} {
    outline: ${props => props.theme.colors.flights.flightItem.focus} solid thin;
  }
`;

const FlightItem = withTheme(
  ({
    isFlightsQueryLoading,
    flightOption,
    selected,
    leg,
    setFlightSelected,
    theme,
    disabled,
    disableFlightIfSameDayBasedOnTime,
    warning,
    deactivateWarning,
    loyaltyDiscountedPrice,
    providerId,
    isRedemptionEnabledForNavitaire,
    flightSearchCriteria,
    googleAnalyticsData,
    oppositeLegSelection,
  }) => {
    const flightEl = useRef(null);
    const { data } = useSSRQuery(FLIGHT_ITEM_QUERY);
    const seatsAvailabilityThreshold = get(
      data,
      'application.configurations.seatsAvailabilityThreshold'
    );

    const translations = useTranslations();
    const { isStadium } = useBookingPath();
    const { cart } = useCart();

    const {
      flightNumberLabel,
      departureTimeLabel,
      arrivalTimeLabel,
      limitedSeatsAvailability,
      availableSeats,
      selectedFlightText,
      isOvernight,
      selectFlightLabel,
      returnFlightBeforeArrival,
      loyaltyDiscountedPriceText,
      loyaltyDiscountedPricePointsText,
      loyaltyDiscountedPriceDefaultText,
    } = translations.flightItem;
    const { operatedByLabel } = translations.common;

    const operatedBy = flightOption?.flight?.operatedBy;
    const validSelected = selected && !disabled;
    const flightRouteDirection =
      flightOption.flight.origin.code + flightOption.flight.destination.code;
    const affinityString = getAffinityString(cart?.order);

    let productList;
    if (googleAnalyticsData.data) {
      const googleAnalyticsFlightMarketData =
        googleAnalyticsData.data.application.googleAnalytics.flightMarkets;
      const selectedFlights = {
        departing: leg === 'departing' ? flightOption : oppositeLegSelection,
        returning: leg === 'returning' ? flightOption : oppositeLegSelection,
      };

      productList = getFlightsRawProducts(
        selectedFlights,
        flightSearchCriteria,
        loyaltyDiscountedPrice,
        affinityString,
        googleAnalyticsFlightMarketData,
        leg
      );
    }

    const seatsAvailability = (withHooks = true) => {
      if (
        flightOption.availableSeatsCount < seatsAvailabilityThreshold ||
        disableFlightIfSameDayBasedOnTime
      ) {
        return (
          <Media>
            {mq => (
              <Text
                size={mq.lt('md') ? 11 : 16}
                weight={500}
                automationHook={withHooks ? SEATS_AVAILABILITY_TEXT.hook : null}
                color={
                  disableFlightIfSameDayBasedOnTime
                    ? theme.colors.flights.flightItem.text
                    : theme.colors.flights.flightItem.alert
                }
              >
                <Markdown
                  source={
                    disableFlightIfSameDayBasedOnTime
                      ? returnFlightBeforeArrival
                      : limitedSeatsAvailability
                  }
                  data={
                    !disableFlightIfSameDayBasedOnTime
                      ? {
                          available_seats:
                            '<strong>' +
                            flightOption.availableSeatsCount +
                            '</strong>',
                          seat:
                            flightOption.availableSeatsCount > 1
                              ? 'seats'
                              : 'seat',
                        }
                      : {}
                  }
                />
              </Text>
            )}
          </Media>
        );
      }
      return (
        <Media>
          {mq => (
            <Text
              size={mq.lt('md') ? 11 : 16}
              automationHook={withHooks ? SEATS_AVAILABLE_TEXT.hook : null}
              weight={500}
            >
              {availableSeats}
            </Text>
          )}
        </Media>
      );
    };

    const renderloyaltyDiscount = mq => {
      if (loyaltyDiscountedPrice === null) {
        return (
          <Box
            h={45}
            pl={mq.lte('md') ? 0 : 8}
            pr={mq.lte('md') ? 0 : 8}
            automationHook={withValue(
              LOYALTY_DISCOUNTED_FLIGHT_PRICE.hook,
              'default-text'
            )}
          >
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
              lh={1.5}
            >
              {loyaltyDiscountedPriceDefaultText}
            </Text>
          </Box>
        );
      }

      if (loyaltyDiscountedPrice >= 0) {
        return (
          <Box
            h={45}
            automationHook={withValue(
              LOYALTY_DISCOUNTED_FLIGHT_PRICE.hook,
              'text'
            )}
          >
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
            >
              <Markdown
                source={loyaltyDiscountedPriceText}
                data={{
                  pointsValue: `$${Number(loyaltyDiscountedPrice).toFixed(2)}`,
                }}
              />
            </Text>
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
              lh={1.5}
            >
              {loyaltyDiscountedPricePointsText}
            </Text>
          </Box>
        );
      }

      return null;
    };

    const setFocus = () => {
      flightEl.current.focus();
    };

    const getPrice = mq => {
      return (
        <div>
          <Box
            d="flex"
            direction={mq.lt('lg') ? 'column' : 'row'}
            wrap={true}
            alignItems="center"
            justifyContent="center"
          >
            {flightOption.strikethruPrice && (
              <Box>
                <del
                  aria-label={'original price: ' + flightOption.strikethruPrice}
                >
                  <Price
                    automationHook={STRIKETHROUGH_FLIGHT_PRICE.hook}
                    value={flightOption.strikethruPrice}
                    currency={'$'}
                    currencyFontSize={mq.lt('md') ? 8 : 13}
                    currencyFontWeight={600}
                    decimalFontSize={mq.lt('md') ? 8 : 12}
                    decimalFontWeight={500}
                    priceFontSize={mq.lt('md') ? 12 : 24}
                    priceFontWeight={500}
                    color={theme.colors.flights.flightItem.lightBlue}
                  />
                </del>
              </Box>
            )}
            <Box aria-label={'discounted price: ' + flightOption.price} pl={1}>
              <Price
                automationHook={FLIGHT_PRICE.hook}
                value={flightOption.price}
                currency={'$'}
                currencyFontSize={mq.lt('md') ? 12 : 23}
                currencyFontWeight={600}
                decimalFontSize={mq.lt('md') ? 12 : 20}
                decimalFontWeight={500}
                priceFontSize={mq.lt('md') ? 20 : 50}
                priceFontWeight={600}
                color={theme.colors.flights.flightItem.lightBlue}
              />
            </Box>
            {/**
             * display loyalty discount banner only for g4 flights
             * this condition will be removed in future
             */}
            {(providerId === 'g4' && renderloyaltyDiscount(mq)) ||
              (providerId === 'other' &&
                isRedemptionEnabledForNavitaire &&
                renderloyaltyDiscount(mq))}
          </Box>
        </div>
      );
    };

    return (
      <Media>
        {mq => (
          <FlightWrapper
            {...(validSelected
              ? hook(SELECTED_FLIGHT.hook)
              : hook(
                  UNSELECTED_FLIGHT.hook,
                  `${leg}_${flightOption.flight.number}`
                ))}
            onClick={e => {
              if (!disabled) {
                if (
                  leg === DEPARTING &&
                  warning?.displayOn === RETURNING &&
                  warning?.key === WARNING_DEPARTING_AFTER_RETURNING
                ) {
                  deactivateWarning();
                }
                setFlightSelected();
                setFocus();
                tracking.trackSelectItem({
                  name: sortOrder.DEFAULT,
                  rawProducts: productList,
                });
              }
              e.stopPropagation();
            }}
          >
            <VisuallyHidden>
              <label htmlFor={flightOption.id + '-radio'}>
                {selectFlightLabel} {flightOption.flight.number}
                {departureTimeLabel}{' '}
                {moment(flightOption.flight.departingTime).format('h:mm')}{' '}
                {moment(flightOption.flight.departingTime).format('a')}
                {arrivalTimeLabel}{' '}
                {moment(flightOption.flight.arrivalTime).format('h:mm')}{' '}
                {moment(flightOption.flight.arrivalTime).format('a')}
                {flightOption.flight.isOvernight && isOvernight}
                {seatsAvailability(false)}
                original price: {flightOption.strikethruPrice}
                discounted price: {flightOption.price}
              </label>
            </VisuallyHidden>
            <HiddenInput
              ref={flightEl}
              onChange={setFlightSelected}
              type="radio"
              name="radio-choice"
              id={flightOption.id + '-radio'}
              value={flightOption.id}
              checked={validSelected}
              disabled={disabled || disableFlightIfSameDayBasedOnTime}
            />
            <FlightBox
              automationHook={
                disabled
                  ? DISABLED_FLIGHT.hook +
                    '_' +
                    `${leg}_${flightOption.flight.number}`
                  : null
              }
              backgroundColor={
                disabled || disableFlightIfSameDayBasedOnTime
                  ? theme.colors.flights.flightItem.lightBackground
                  : '#fff'
              }
            >
              <FlightNumberBox
                p={8}
                pl={mq.lt('md') ? 4 : 8}
                pr={mq.lt('md') ? 4 : 8}
                backgroundColor={
                  theme.colors.flights.flightItem.lightBackground
                }
              >
                <LabelHeader {...hook(FLIGHT_NUMBER_LABEL.hook)}>
                  {flightNumberLabel}
                </LabelHeader>
                <VerticalSpacing size={2} />
                <Box>
                  <Popover
                    content={<PopoverContent flightOption={flightOption} />}
                    label={'popover'}
                    arrowSize={mq.lte('sm') ? 0 : 50}
                    arrowOffset="-50px"
                    backgroundColor={theme.colors.popover.background}
                    maxWidth="530px"
                    showClose={mq.lte('sm')}
                    automationHook={
                      validSelected
                        ? withValue(OTP_POPOVER.hook, `selected_${leg}`)
                        : undefined
                    }
                  >
                    <GAClickEventTracker
                      onClick={() =>
                        tracking.trackEvent(
                          'site_click_tracking',
                          'Flight Chooser',
                          `OTP | ${flightRouteDirection} Flight ${flightOption.flight.number}`
                        )
                      }
                    >
                      <FlightNumberText
                        size={mq.lt('md') ? 16 : 22}
                        color={theme.colors.flights.flightItem.lightBlue}
                        weight={600}
                        automationHook={FLIGHT_NUMBER.hook}
                      >
                        {flightOption.flight.number}
                      </FlightNumberText>
                    </GAClickEventTracker>
                  </Popover>
                </Box>
              </FlightNumberBox>
              <FlightDetailsBox
                d="flex"
                direction={mq.lte('md') ? 'column' : 'row'}
                pt={8}
                pl={mq.lte('md') ? 2 : 8}
                pr={mq.lte('md') ? 2 : 8}
                pb={mq.lte('md') ? 3 : 8}
              >
                <TimeBox d="flex" pr={mq.lte('md') ? 0 : 8}>
                  <DepartureBox>
                    <LabelHeader {...hook(FLIGHT_DEPARTURE_LABEL.hook)}>
                      {departureTimeLabel}
                    </LabelHeader>
                    <VerticalSpacing size={2} />

                    <FlightTime {...hook(FLIGHT_DEPARTURE_TIME.hook)}>
                      {moment(flightOption.flight.departingTime).format('h:mm')}
                      <span>
                        {moment(flightOption.flight.departingTime)
                          .format('a')
                          .toUpperCase()}
                      </span>
                    </FlightTime>
                    {!mq.lte('md') && operatedBy && (
                      <FlightOperator
                        operatedBy={operatedBy}
                        operatedByLabel={operatedByLabel}
                        display="flex"
                        weight={500}
                      ></FlightOperator>
                    )}
                  </DepartureBox>
                  <ArrivalBox>
                    <LabelHeader {...hook(FLIGHT_ARRIVAL_LABEL.hook)}>
                      {arrivalTimeLabel}
                    </LabelHeader>
                    <VerticalSpacing size={2} />
                    <FlightTime {...hook(FLIGHT_ARRIVAL_TIME.hook)}>
                      {moment(flightOption.flight.arrivalTime).format('h:mm')}
                      <span>
                        {moment(flightOption.flight.arrivalTime)
                          .format('a')
                          .toUpperCase()}
                      </span>
                    </FlightTime>
                    {flightOption.flight.isOvernight && (
                      <OvernightText
                        automationHook={OVERNIGHT_FLIGHT_LABEL.hook}
                        size={mq.lte('md') ? 10 : 12}
                        color={theme.colors.flights.flightItem.lightBlue}
                      >
                        {isOvernight}
                      </OvernightText>
                    )}
                  </ArrivalBox>
                </TimeBox>
                {mq.lte('md') && operatedBy && (
                  <FlightOperator
                    operatedBy={operatedBy}
                    operatedByLabel={operatedByLabel}
                    display="flex"
                    weight={500}
                  ></FlightOperator>
                )}
                <SeatsTextBox
                  pr={mq.lte('md') ? 0 : 8}
                  pt={mq.lte('md') ? (operatedBy ? 2 : 4) : 0}
                >
                  {seatsAvailability()}
                </SeatsTextBox>
              </FlightDetailsBox>
              <SelectFlightBox selected={validSelected}>
                <PriceBox {...hook(PRICE_BOX.hook)}>
                  {!isFlightsQueryLoading && validSelected && (
                    <p>{selectedFlightText}</p>
                  )}
                  <Box
                    className="prices"
                    d="flex"
                    direction={mq.lt('lg') ? 'column' : 'row'}
                  >
                    {isFlightsQueryLoading ? (
                      <Spinner
                        size="small"
                        appearance="dark"
                        automationHook={withValue('price-box', 'spinner')}
                      />
                    ) : !isStadium ? (
                      getPrice(mq)
                    ) : (
                      <p
                        style={
                          mq.lt('md')
                            ? { paddingTop: '20px' }
                            : { paddingTop: '2px' }
                        }
                      >
                        <Text
                          weight={600}
                          wrap={true}
                          size={mq.lt('md') ? 20 : 50}
                        >
                          <span style={{ fontSize: '15px' }}>{'Included'}</span>
                        </Text>
                      </p>
                    )}
                  </Box>
                </PriceBox>
              </SelectFlightBox>
            </FlightBox>
          </FlightWrapper>
        )}
      </Media>
    );
  }
);
FlightItem.propTypes = propTypes;

export default FlightItem;